import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { IconPlus, IconPencil, IconBook, IconClock } from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

/**
 * SombraCoursesAdmin
 * Panel embebido en el módulo Sello Sombra para que Mariela (admin)
 * gestione los cursos con track="sombra" sin salir del módulo.
 * Reutiliza los endpoints de Aula como fuente única de verdad.
 */
export default function SombraCoursesAdmin() {
  const { lang } = useI18n();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/aula/courses?track=sombra");
        setItems(data);
      } catch (e) { setError(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <p className="eyebrow">{lang === "es" ? "Formación Sombra" : "Sombra training"}</p>
          <p className="text-muted text-[13px] mt-1">
            {lang === "es"
              ? "Cursos dirigidos a Maestras Sombra. Se listan y compran también desde Aula."
              : "Courses aimed at Sombra teachers. Also purchasable via Aula."}
          </p>
        </div>
        <button
          className="btn"
          onClick={() => nav("/app/aula/nuevo?track=sombra")}
          data-testid="sello-courses-new"
        >
          <IconPlus size={13} /> {lang === "es" ? "Nuevo curso Sombra" : "New Sombra course"}
        </button>
      </div>

      {error && <p className="text-errtext text-[13px] mb-3">{error}</p>}
      {loading ? (
        <p className="text-tert text-[12px] font-mono uppercase">Cargando…</p>
      ) : items.length === 0 ? (
        <div className="card text-center py-10" data-testid="sello-courses-empty">
          <IconBook size={22} className="mx-auto text-tert mb-2" />
          <p className="text-tert text-[13px]">
            {lang === "es"
              ? "Aún no hay cursos con track sombra. Crea el primero."
              : "No sombra-track courses yet."}
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="sello-courses-list">
          {items.map((c) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mod-card relative cursor-pointer"
              onClick={() => nav(`/app/aula/${c.id}`)}
              data-testid={`sello-course-${c.id}`}
            >
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); nav(`/app/aula/edit/${c.id}`); }}
                className="absolute top-3 right-3 text-tert hover:text-teal-deep p-1"
                aria-label={lang === "es" ? "Editar curso" : "Edit course"}
                data-testid={`sello-course-edit-${c.id}`}
              >
                <IconPencil size={14} />
              </button>
              <div className="flex items-center gap-2 mb-2">
                <span className="tag">Sombra</span>
                <span className="tag tag-honey">{c.level}</span>
                {c.is_capsule && <span className="tag" style={{ background: "#EEEDFE", color: "#3C3489" }}>{lang === "es" ? "Cápsula" : "Capsule"}</span>}
                {c.status === "draft" && <span className="tag" style={{ background: "#F3EDE1", color: "#854F0B" }}>{lang === "es" ? "Borrador" : "Draft"}</span>}
              </div>
              <h3 className="font-display text-[17px] font-medium">{c.title}</h3>
              {c.subtitle && <p className="text-tert text-[12.5px] mt-1">{c.subtitle}</p>}
              <p className="text-muted text-[13px] mt-2 leading-relaxed line-clamp-3">{c.description}</p>
              <div className="flex items-center gap-3 mt-3 text-tert text-[11.5px]">
                <span className="flex items-center gap-1"><IconClock size={12} /> {c.duration_min || "—"} min</span>
                <span>· {c.total_lessons || 0} {lang === "es" ? "lecciones" : "lessons"}</span>
              </div>
              <div className="flex items-center justify-between mt-3">
                {c.is_free || (Number(c.price || 0) === 0) ? (
                  <span className="tag" style={{ background: "#E7F3EE", color: "#0A5C3F" }}>{lang === "es" ? "Gratuito" : "Free"}</span>
                ) : (
                  <span className="font-display text-[16px] font-medium text-teal-deep">${Number(c.price).toFixed(2)}</span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
